import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompanyName } from "@/lib/company";
import { GraphiteScope, useGraphite } from "@/components/Graphite";
import { usd } from "@/components/common";
import { useFlags } from "@/lib/flags";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import { AlertTriangle, Loader2 } from "lucide-react";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

// Owner → Account (and Settings): delete the company's account (docs/ACCOUNT_LIFECYCLE_PLAN.md slice D). The backend works out what
// comes back (the plan's unused days, the unused paid top-ups); the owner types the company's name and their password.
export function DeleteAccountCard() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const preview = useQuery({ queryKey: ["account-delete"], queryFn: async () => (await api.get("/account/delete")).data,
    enabled: open, staleTime: 0 });
  const del = useMutation({
    mutationFn: async ({ password, name }) => (await api.post("/account/delete", { password, company_name: name })).data,
    onSuccess: ({ delete_after, refund_usd }) => {
      setOpen(false); refresh();
      toast.success(`Your account is deleted on ${day(delete_after)}${refund_usd > 0 ? `; ${usd(refund_usd)} is on its way back` : ""}. You can undo it until then.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const p = preview.data;
  const refund = p ? p.plan_refund_usd + p.topup_refund_usd : 0;
  const wallet = useFlags().wallet;  // the AI wallet (FEATURE_WALLET): the whole balance comes back
  // Graphite: danger is a text colour with its icon and a word, never a tinted box
  const g = useGraphite();
  const company = useCompanyName();
  // the handoff's danger zone: a bordered card (no fill), the danger colour only on the button
  return (
    <section className="rounded-[10px] border px-5 py-[18px] flex flex-wrap items-center justify-between gap-x-5 gap-y-3" data-testid="delete-account-card">
      <div className="flex-[1_1_320px] flex flex-col gap-0.5">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Delete {company ? `${company}'s` : "your"} account</span>
        {user?.account_deletion?.delete_after ? (  // asked already: the banner above has the date and Undo
          <span className="text-sm text-muted-foreground" data-testid="delete-account-scheduled">
            Your account will be deleted on {day(user.account_deletion.delete_after)}. To keep it, use Undo at the top of the page.</span>
        ) : (
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">
            Sessions stop, your workspace server is deleted once its backups are checked, and what you haven't used is refunded.</span>
        )}
      </div>
      {!user?.account_deletion?.delete_after && (
        <Button variant="outline" className="text-gr-danger" onClick={() => setOpen(true)} data-testid="delete-account">Delete account</Button>
      )}
      <ConfirmDestructive open={open} onOpenChange={setOpen} title="Delete your company's account?" action="Delete account"
        confirmName={p?.company_name} pending={del.isPending || preview.isLoading}
        onConfirm={(password, name) => del.mutate({ password, name })}
        warning="You can undo it for 7 days; after that it can't be undone.">
        {preview.isLoading ? <p><Loader2 className="inline w-4 h-4 animate-spin mr-1" />Working out your refund…</p>
          : preview.isError ? <p className={g ? "text-gr-danger" : "text-red-500"}>{apiErr(preview.error)}</p> : p && (
          <>
            <p><strong>At once:</strong> your plan ends, sessions are locked (their work is backed up), credits stop, and
              your team can't sign in. You can still sign in to undo it.</p>
            <p data-testid="delete-account-refund"><strong>Refunded:</strong> {usd(refund)}
              {p.plan_refund_usd > 0 && ` (${usd(p.plan_refund_usd)} for the days left of your plan`}
              {p.topup_refund_usd > 0 && `${p.plan_refund_usd > 0 ? ", " : " ("}${usd(p.topup_refund_usd)} ${wallet ? "from your AI wallet" : "of unused top-ups"}`}
              {(p.plan_refund_usd > 0 || p.topup_refund_usd > 0) && ")"}, to your card by Lemon Squeezy.{wallet ? " Some fees aren't refundable, like server time already used." : " Credits we added for free aren't refunded."}</p>
            {p.owed_usd > 0 && (  // a scheduled investor update ran on credit (design_handoff_founder_assistant screen 9)
              <p className={g ? "text-gr-warning" : "text-amber-600"} data-testid="delete-account-owed">
                Your balance is -${p.owed_usd.toFixed(2)}. It comes off your refund.</p>
            )}
            <p><strong>In {p.days} days:</strong> your workspace server, projects, sessions, shared memory and everyone's
              account are deleted. What your sessions pushed stays in your repositories; receipts stay with Lemon Squeezy.</p>
          </>
        )}
      </ConfirmDestructive>
    </section>
  );
}

// For owners while the account is being deleted: the date, and Undo (with the password).
export function DeletionBanner() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const undo = useMutation({
    mutationFn: async () => (await api.post("/account/undo-delete", { password })).data,
    onSuccess: () => { setOpen(false); setPassword(""); refresh(); toast.success("Your account isn't being deleted any more"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const when = user?.account_deletion?.delete_after;
  if (!when) return null;
  return (
    // Graphite: a flat notice with the danger icon, never a tinted box (it sits in the frame, above
    // pages that may not have moved to Graphite yet, so it brings its own scope)
    <GraphiteScope className="mb-6 rounded-lg border bg-card px-5 py-4 flex items-center justify-between gap-4"
      data-testid="deletion-banner">
      <span><AlertTriangle className="inline w-4 h-4 mr-2 -mt-0.5 text-gr-danger" aria-hidden="true" />
        Your account will be deleted on <strong>{day(when)}</strong>.
        Your team can't sign in and sessions are stopped.</span>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="undo-deletion">Undo</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPassword(""); }}>
        <DialogContent>
          <form onSubmit={(e) => { e.preventDefault(); if (password) undo.mutate(); }}>
            <DialogHeader>
              <DialogTitle>Keep your account?</DialogTitle>
              <DialogDescription>Your team can sign in again and credits work again. Your plan stays ended (its refund
                was made): choose a new one in Store if you need one.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 my-4">
              <Label htmlFor="undo-password">Your password</Label>
              <Input id="undo-password" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} autoFocus data-testid="undo-password" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={!password || undo.isPending} data-testid="undo-confirm">
                {undo.isPending && <Loader2 className="w-4 h-4 animate-spin" />}Keep my account</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </GraphiteScope>
  );
}

// Admin lists (Companies, Store): a company whose owner is deleting its account, or whose account was deleted.
export function DeletionBadge({ deletion, deletedAt }) {
  const g = useGraphite();
  if (deletedAt) return <Badge variant="outline" className={g ? "ml-2" : "ml-2 text-[10px]"}
    title="Only its money records and the audit log are kept">deleted {day(deletedAt)}</Badge>;
  if (!deletion) return null;
  const [text, title] = deletion.status === "scheduled"
    ? [`deleting on ${day(deletion.delete_after)}`, deletion.error
      ? `The last try failed (${deletion.error}): it's tried again every hour` : "A founder can undo it until then"]
    : deletion.status === "deleting" ? ["deleting now", "Its data is being deleted"]
    : ["deletion unfinished", "The founder's request stopped while refunding; it carries on when they ask again"];
  return <Badge variant="outline" className={g ? "ml-2 text-gr-danger" : "ml-2 text-[10px] text-red-500"} title={title}
    data-testid="deletion-badge">{text}</Badge>;
}
